import type { IsoDateTime } from "../primitives";
import {
  ORDER_STAGE_LABELS,
  type OrderStage,
  type OrderStageChange,
} from "./stage";

/**
 * The order's journey (#682): how long it spent in each stage.
 *
 * The stage history says when an order *entered* each stage; this turns those
 * rows into segments with a length, once, so the order page, the API and the
 * fixture adapter cannot disagree about a duration. It is pure: the same rows
 * and the same `now` give the same answer, which is what the tests lean on.
 *
 * Durations are the gaps between consecutive rows, so they are only as good as
 * the rows. The recorder sweeps every five minutes, so a gap is right to
 * within that; history before 10 Oct 2026 was rebuilt from scans and tags and
 * says so with `approximate`.
 */

/**
 * The stages that end a journey. Not the same list as the shipment's final
 * stages: this is about the order. Only the *last* row can end it; a return
 * added after a delivery carries on from Delivered like any other stage.
 */
export const JOURNEY_FINAL_STAGES: readonly OrderStage[] = [
  "delivered",
  "rto_received",
  "cancelled",
];

/**
 * The first row of an order's history is normally within minutes of placing
 * it. Later than this, the time before it is unknown and the first segment is
 * stretched back to placement and marked approximate.
 */
export const JOURNEY_LATE_START_SECONDS = 3600;

/** The narrowest a segment is drawn, as a share of the bar. */
export const JOURNEY_MIN_SHARE = 0.02;

export interface JourneySegment {
  stage: OrderStage;
  enteredAt: IsoDateTime;
  /** When the next stage began; null for the last segment. */
  leftAt: IsoDateTime | null;
  /** Whole seconds. 0 for the stage that ends the journey: it is a marker, not a length. */
  seconds: number;
  /** The start or the length is a best guess (rebuilt, or the first row was late). */
  approximate: boolean;
  /** The journey ends here (Delivered, RTO received, Cancelled). */
  final: boolean;
  /** Why a person put the order in this stage, when one did (#591). */
  manualReason: string | null;
}

export interface OrderJourney {
  segments: JourneySegment[];
  /** Placement to the end, or to now while the order is still open. */
  totalSeconds: number;
  /** When the journey ended; null while the order is open. */
  endedAt: IsoDateTime | null;
}

const seconds = (ms: number) => Math.max(0, Math.floor(ms / 1000));

/**
 * Segments from an order's stage history, in any order. Rows at the same
 * moment keep the order they were given in.
 *
 * - Two rows in a row for one stage are one segment.
 * - A stage that comes round again is a segment of its own, never merged with
 *   the earlier one.
 * - The last segment is open and grows with `now`, unless its stage ends the
 *   journey, when it is a marker with no length.
 * - A row dated after `now`, or before the one it follows, never produces a
 *   negative length.
 */
export function journeySegments(
  history: readonly OrderStageChange[],
  placedAt: IsoDateTime,
  now: Date,
): OrderJourney {
  const rows = history
    .map((row) => ({ ...row, at: new Date(row.enteredAt).getTime() }))
    .sort((a, b) => a.at - b.at);

  const merged: typeof rows = [];
  for (const row of rows) {
    const last = merged[merged.length - 1];
    if (last && last.stage === row.stage) {
      last.approximate = last.approximate || row.approximate;
      last.manualReason ??= row.manualReason;
    } else {
      merged.push({ ...row });
    }
  }
  const first = merged[0];
  if (!first) return { segments: [], totalSeconds: 0, endedAt: null };

  // Stretch the first stage back to placement. A small gap is the sweep's
  // delay; a big one is time nobody recorded, so say so.
  const placed = new Date(placedAt).getTime();
  if (placed < first.at) {
    if (seconds(first.at - placed) > JOURNEY_LATE_START_SECONDS) {
      first.approximate = true;
    }
    first.at = placed;
  }

  const nowMs = now.getTime();
  const segments: JourneySegment[] = merged.map((row, i) => {
    const next = merged[i + 1];
    const isLast = next === undefined;
    const final = isLast && JOURNEY_FINAL_STAGES.includes(row.stage);
    const end = next ? next.at : nowMs;
    return {
      stage: row.stage,
      enteredAt: new Date(row.at).toISOString(),
      leftAt: next ? new Date(next.at).toISOString() : null,
      seconds: final ? 0 : seconds(end - row.at),
      approximate: row.approximate,
      final,
      manualReason: row.manualReason,
    };
  });

  const last = segments[segments.length - 1];
  return {
    segments,
    totalSeconds: segments.reduce((sum, s) => sum + s.seconds, 0),
    endedAt: last?.final ? last.enteredAt : null,
  };
}

/** `7 d`, `5 d 4 h`, `3 h 20 min`, `12 min`; nothing finer than a minute. */
export function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 1) return "under 1 min";
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  if (hours > 0) return mins > 0 ? `${hours} h ${mins} min` : `${hours} h`;
  return `${mins} min`;
}

const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** The same length in words, for a screen reader: `5 days 4 hours`. */
export function describeDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 1) return "under a minute";
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0)
    return hours > 0
      ? `${unit(days, "day")} ${unit(hours, "hour")}`
      : unit(days, "day");
  if (hours > 0)
    return mins > 0
      ? `${unit(hours, "hour")} ${unit(mins, "minute")}`
      : unit(hours, "hour");
  return unit(mins, "minute");
}

/**
 * Every segment in one sentence, for the bar's `aria-label` and the list's
 * tooltip: `New 2 hours, Confirmed 7 days, In transit 5 days so far`.
 */
export function describeJourney(journey: OrderJourney): string {
  if (journey.segments.length === 0) return "No stage history yet";
  return journey.segments
    .map((segment) => {
      const label = ORDER_STAGE_LABELS[segment.stage];
      if (segment.final)
        return `${label} after ${describeDuration(journey.totalSeconds)}`;
      const open = segment.leftAt === null ? " so far" : "";
      return `${label} ${segment.approximate ? "about " : ""}${describeDuration(segment.seconds)}${open}`;
    })
    .join(", ");
}

export interface JourneyWidths {
  /** Each segment's share of the bar, adding up to 1. */
  shares: number[];
  /** A short segment was widened to stay visible, so the bar is not to scale. */
  floored: boolean;
}

/**
 * How wide to draw each segment: in proportion to its time, but never under
 * `JOURNEY_MIN_SHARE`, so a ten-minute stage next to a ten-day one is still
 * there to hover. The marker that ends a journey has no length and no width.
 */
export function journeyShares(
  segments: readonly Pick<JourneySegment, "seconds" | "final">[],
  minShare: number = JOURNEY_MIN_SHARE,
): JourneyWidths {
  const drawn = segments.map((s) => !s.final);
  const count = drawn.filter(Boolean).length;
  if (count === 0) return { shares: segments.map(() => 0), floored: false };

  const total = segments.reduce((sum, s) => sum + (s.final ? 0 : s.seconds), 0);
  // Nothing has a length yet: share the bar equally rather than divide by 0.
  let shares = segments.map((s, i) =>
    !drawn[i] ? 0 : total === 0 ? 1 / count : s.seconds / total,
  );

  // Widen what is too thin, taking the room from the rest in proportion; that
  // can push another below the floor, so go again until nothing changes.
  const pinned = new Set<number>();
  for (let pass = 0; pass < segments.length; pass++) {
    const thin = shares
      .map((share, i) =>
        drawn[i] && !pinned.has(i) && share < minShare ? i : -1,
      )
      .filter((i) => i >= 0);
    if (thin.length === 0) break;
    for (const i of thin) pinned.add(i);
    const room = 1 - pinned.size * minShare;
    const freeTotal = segments.reduce(
      (sum, s, i) => sum + (drawn[i] && !pinned.has(i) ? s.seconds : 0),
      0,
    );
    const freeCount = count - pinned.size;
    shares = segments.map((s, i) => {
      if (!drawn[i]) return 0;
      if (pinned.has(i)) return minShare;
      if (freeCount === 0) return 0;
      return freeTotal === 0
        ? room / freeCount
        : (s.seconds / freeTotal) * room;
    });
  }
  // Only reachable with every segment under the floor: they share it equally.
  const sum = shares.reduce((a, b) => a + b, 0);
  if (sum > 0 && Math.abs(sum - 1) > 1e-9) shares = shares.map((x) => x / sum);
  return { shares, floored: pinned.size > 0 };
}
